import type { Point2d, RightAnglePoints } from "@logic/lib/2d"
import {
  findClosestLineSnapPoint,
  whereDoLinesIntersectUsingPoints,
} from "@logic/lib/2d"
import { atom, selector, useRecoilTransaction_UNSTABLE } from "recoil"

import type { TransactionOperation } from "."
import { getMousePositionOnScreen, readonlyTransactors } from "."
import { shiftIsDownState } from "../../inputs"
import { edgeIndex } from "../edges"
import { projectNode, nodeIndex } from "../nodes"
import {
  nodeSelectionState,
  getNodeSelectionState,
  lastOf,
} from "../nodeSelection"

export interface PreviewNodeContents {
  previewNode: Point2d
  closestNodeSnap: Point2d | null
  whereLinesIntersect: Point2d | null
  possibleAngles: { angle: number; id: string }[]
  previousNodeIdWeDrawFrom: string | null
  rightAnglePoints: RightAnglePoints | null
}

export const guideAngleSwapState = atom({
  key: `possibleAngles`,
  default: {
    index: 0,
    angles: [] as { angle: number; id: string }[],
    previousNodeIdWeDrawFrom: null as string | null,
  },
})

export const getPreviewNodeSnapPoint: TransactionOperation<
  undefined,
  PreviewNodeContents
> = ({ get }) => {
  const shiftIsDown = get(shiftIsDownState)
  const mouseOnScreen = getMousePositionOnScreen(readonlyTransactors(get))
  const nodeSelection = getNodeSelectionState(readonlyTransactors(get))
  const nodeIdWeDrawFrom = lastOf(nodeSelection)
  const contents: PreviewNodeContents = {
    previewNode: { x: mouseOnScreen.x, y: mouseOnScreen.y },
    closestNodeSnap: null as Point2d | null,
    whereLinesIntersect: null as Point2d | null,
    possibleAngles: [],
    previousNodeIdWeDrawFrom: nodeIdWeDrawFrom,
    rightAnglePoints: null,
  }

  if (shiftIsDown || !nodeIdWeDrawFrom) {
    return contents
  }

  const angleSwap = get(guideAngleSwapState)

  // get angle of previous edge
  const allEdgeIds = get(edgeIndex)
  let angleOfLastEdge = 0

  for (const edgeId of allEdgeIds) {
    const nodeIds = edgeId.split(`/`)
    if (nodeIds[0] === nodeIdWeDrawFrom || nodeIds[1] === nodeIdWeDrawFrom) {
      const otherNodeId = nodeIds[nodeIdWeDrawFrom === nodeIds[0] ? 1 : 0]
      const nodeWeDrawFrom = projectNode(get, nodeIdWeDrawFrom)
      const otherNode = projectNode(get, otherNodeId)
      const angle = Math.atan2(
        otherNode.y - nodeWeDrawFrom.y,
        otherNode.x - nodeWeDrawFrom.x,
      )
      contents.possibleAngles.push({ angle: angle, id: edgeId })
    }
  }

  angleOfLastEdge = contents.possibleAngles[angleSwap.index]?.angle
  if (angleOfLastEdge === undefined) return contents

  const lastPoint = projectNode(get, nodeIdWeDrawFrom)

  const { snapPoint: mouseSnapPoint, rightAnglePoints } =
    findClosestLineSnapPoint(mouseOnScreen, lastPoint, angleOfLastEdge, 36)

  contents.rightAnglePoints = rightAnglePoints
  contents.previewNode = mouseSnapPoint

  const allNodeIds = get(nodeIndex)

  for (const nodeId of allNodeIds) {
    if (nodeId === nodeIdWeDrawFrom) continue

    const node = projectNode(get, nodeId)
    const nodeSnapPoint = findClosestLineSnapPoint(
      mouseSnapPoint,
      node,
      angleOfLastEdge,
      2,
    ).snapPoint

    const intersectionPoint = whereDoLinesIntersectUsingPoints(
      lastPoint,
      mouseSnapPoint,
      node,
      nodeSnapPoint,
    )

    if (!intersectionPoint) continue
    if (
      Math.abs(intersectionPoint.x - mouseSnapPoint.x) < 5 &&
      Math.abs(intersectionPoint.y - mouseSnapPoint.y) < 5
    ) {
      Object.assign(contents, {
        closestNodeSnap: node,
        whereLinesIntersect: intersectionPoint,
        previewNode: intersectionPoint,
      })
      break
    } else {
      Object.assign(contents, {
        closestNodeSnap: null,
        whereLinesIntersect: null,
      })
    }
  }

  return contents
}

const swapPreviewEdgeAngle: TransactionOperation = ({ set }) => {
  set(guideAngleSwapState, (prev) => {
    const { index, angles } = prev
    if (index === angles.length - 1) {
      return { ...prev, index: 0, angles }
    }

    return { ...prev, index: index + 1, angles }
  })
}

export const useSwapPreviewEdgeAngle = (): VoidFunction =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => swapPreviewEdgeAngle(transactors),
  )

export const nodeSnapPointState = selector<PreviewNodeContents>({
  key: `EdgeSnapAngleState`,
  get: ({ get }) => {
    const contents = getPreviewNodeSnapPoint(readonlyTransactors(get))

    return contents
  },
})
